import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './App.css';
import Layout from './Layout';
import Homepage from './Homepage';
import ForBusinesses from './ForBusinesses';
import ForJobSeekers from './ForJobSeekers';
import About from './About';
import Blog from './Blog';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import ContactUs from './ContactUs';
import SignIn from './SignIn.jsx';
import SignUp from './SignUp.jsx';
import RegularUserSignUp from './RegularUserSignUp.jsx';
import BusinessUserSignUp from './BusinessUserSignUp.jsx';
import RegularUserSignIn from './RegularUserSignIn.jsx';
import BusinessUserSignIn from './BusinessUserSignIn.jsx';
import UserProfileEdit from './UserProfileEdit';
import BlogPost from './components/blog/BlogPost';
// Import dashboard components
import { 
  DashboardLayout, 
  DashboardHome, 
  OptimizedJobMap, 
  ChatDashboard, 
  Updates, 
  ProfileDashboard 
} from './components/dashboard';
import Settings from './components/dashboard/Settings';
// Import new list pages
import JobListPage from './components/pages/JobListPage';
import BusinessListPage from './components/pages/BusinessListPage';
import JobDetailPage from './components/pages/JobDetailPage';
import BusinessDetailPage from './components/pages/BusinessDetailPage';
// Import Firebase configuration
import { app, analytics } from './firebase/config';
// Import AuthProvider
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
// Import fix utilities for debugging (available in browser console)
import './utils/fixApplications';
import './utils/fixMissingBusinessId';
import { 
  BusinessDashboardLayout, 
  BusinessDashboardHome, 
  BusinessJobs, 
  BusinessJobDetail,
  BusinessApplicants, 
  BusinessChat, 
  BusinessProfile
} from './components/businessDashboard';
import BusinessSettings from './components/businessDashboard/BusinessSettings';

// Auth route component to handle redirections based on user role
const AuthRoute = () => {
  const { isAuthenticated, userRole } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (userRole === 'business_user') {
    return <Navigate to="/business-dashboard" />;
  }

  // Redirecting to dashboard main page
  return <Navigate to="/dashboard/home" />;
};

// Component to protect routes that require authentication
const RequireAuth = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

function App() {
    useEffect(() => {
      // Firebase is initialized in the config file
      console.log('Firebase initialized');
    }, []);

    return (
      <AuthProvider>
        <div className="App">
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Homepage />} />
              <Route path="/job-seekers" element={<ForJobSeekers />} />
              <Route path="/businesses" element={<ForBusinesses />} />
              <Route path="/about" element={<About />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:postId" element={<BlogPost />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              
              {/* Public Job and Business List Routes */}
              <Route path="/jobs" element={<JobListPage />} />
              <Route path="/businesses-list" element={<BusinessListPage />} />
            </Route>
            
            {/* Authentication Routes */}
            <Route path="/login" element={<SignIn />} />
            <Route path="/login/regular" element={<RegularUserSignIn />} />
            <Route path="/login/business" element={<BusinessUserSignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/signup/regular" element={<RegularUserSignUp />} />
            <Route path="/signup/business" element={<BusinessUserSignUp />} />
            
            {/* Profile Edit Route */}
            <Route path="/profile/edit" element={
              <RequireAuth>
                <UserProfileEdit />
              </RequireAuth>
            } />
            
            {/* Handle the /dashboard root path to redirect based on auth status */}
            <Route path="/dashboard" element={<AuthRoute />} />
            
            {/* Dashboard Routes */}
            <Route path="/dashboard" element={
              <RequireAuth>
                <DashboardLayout />
              </RequireAuth>
            }>
              <Route index element={<Navigate to="/dashboard/home" replace />} />
              <Route path="home" element={<DashboardHome />} />
              <Route path="jobs" element={<JobListPage />} />
              <Route path="jobs/:jobId" element={<JobDetailPage />} />
              <Route path="businesses" element={<BusinessListPage />} />
              <Route path="businesses/:businessId" element={<BusinessDetailPage />} />
              <Route path="map" element={<OptimizedJobMap />} />
              <Route path="chat" element={<ChatDashboard />} />
              <Route path="updates" element={<Updates />} />
              <Route path="profile" element={<ProfileDashboard />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            {/* Business Dashboard Routes */}
            <Route path="/business-dashboard" element={<BusinessDashboardLayout />}>
              <Route index element={<BusinessDashboardHome />} />
              <Route path="jobs" element={<BusinessJobs />} />
              <Route path="jobs/:jobId" element={<BusinessJobDetail />} />
              <Route path="applicants" element={<BusinessApplicants />} />
              <Route path="chat" element={<BusinessChat />} />
              <Route path="profile" element={<ProfileDashboard />} />
              <Route path="business-profile" element={<BusinessProfile />} />
              <Route path="settings" element={<BusinessSettings />} />
            </Route>
          </Routes>
          
          {/* Toast notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                style: {
                  background: '#10b981',
                },
              },
              error: {
                style: {
                  background: '#ef4444',
                },
              },
            }}
          />
        </div>
      </AuthProvider>
    );
  }

export default App;